import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { auth, signInWithGoogle } from '../firebase-config';
import { 
  signInWithEmailAndPassword, 
  onAuthStateChanged,
  fetchSignInMethodsForEmail 
} from 'firebase/auth';

const { Title } = Typography;

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user is already signed in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/edit');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Check sign-in methods for this email
      const signInMethods = await fetchSignInMethodsForEmail(auth, values.email);
      
      if (signInMethods.includes('google.com') && !signInMethods.includes('password')) {
        message.error('This email is registered with Google. Please use Google Sign In.');
        setLoading(false);
        return;
      }

      await signInWithEmailAndPassword(auth, values.email, values.password);
      message.success('Welcome back! 🎉');
      navigate('/edit');
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific error cases
      switch (error.code) {
        case 'auth/user-not-found':
          message.error('No account found with this email. Please sign up first.');
          break;
        case 'auth/wrong-password':
          message.error('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          message.error('Please enter a valid email address.');
          break;
        case 'auth/user-disabled':
          message.error('This account has been disabled. Please contact support.');
          break;
        case 'auth/too-many-requests':
          message.error('Too many failed attempts. Please try again later.');
          break;
        default:
          message.error('Login failed 😕 Please check your credentials');
      }
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithGoogle();
      const email = result.user.email;

      // Check if user exists with email/password
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.includes('password') && !signInMethods.includes('google.com')) {
        // User exists with email/password but not Google
        message.error('Please sign in with your email and password instead.');
        await auth.signOut();
        return;
      }

      message.success('Welcome back! 🎉');
      navigate('/edit');
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        message.error('Please use email/password to sign in.');
      } else {
        message.error('Google sign-in failed 😕');
      }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Branding Section */}
      <div className="absolute top-0 left-0 right-0 text-center py-8">
      <h1 className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-tr from-pink-600/30 to-indigo-800/30">
          CarouselPost.in
        </h1>
        <p className="mt-2 text-lg text-gray-600 font-medium">
          Transform Your Ideas into Beautiful Slides ✨
        </p>
      </div>

      {/* Main Content - Keeping the original container */}
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/90 rounded-2xl shadow-xl p-8 space-y-6 border border-white/50">
          <div className="text-center space-y-2">
            <Title level={2} className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Welcome Back! ✨
            </Title>
            <p className="text-gray-500">Let's create something amazing today</p>
          </div>

          <Form
            name="signin"
            onFinish={onFinish}
            layout="vertical"
            className="space-y-4"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Email"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please input your password!' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
                className="rounded-lg"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg"
              >
                Sign In 🚀
              </Button>
            </Form.Item>
          </Form>

          <Divider plain>or</Divider>

          <Button
            onClick={handleGoogleSignIn}
            block
            size="large"
            icon={<GoogleOutlined />}
            className="rounded-lg border-2 hover:border-purple-400 hover:text-purple-600"
          >
            Continue with Google
          </Button>

          <div className="text-center mt-4">
            <span className="text-gray-500">Don't have an account? </span>
            <Link to="/signup" className="text-purple-600 hover:text-purple-800 font-medium">
              Sign Up
            </Link>
          </div>
        </div>
      </div>

      {/* Add subtle pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
    </div>
  );
};

export default SignIn;
